import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Col from 'react-bootstrap/Col'
import DataGrid from 'react-data-grid'
import styled from 'styled-components'
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom'
import { getApps, getAppIconUrl } from 'app/services/client'
import { RootState } from 'app/state/types'
import { setApps, setIsLoading } from 'app/state/appSlice'
import { columnsConfig } from './columnsConfig'
import { SearchAppInfo } from 'domain/SearchAppInfo'
import { APP_ROUTES } from 'app/components/AppRouter/routes'
import NoGridData from 'app/components/NoGridData'
import { isTokenExpired } from 'app/utils/hooks'
import SearchPagination from 'app/components/SearchPagination'
import { handleServerAPIError } from 'app/services/utils'

const Container = styled.div`
    margin-top: 10px;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const APPS_LIMIT = 20

const AppsList = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [pageCount, setPageCount] = useState<number>(1)
    const { cdnInfo } = useSelector((state: RootState) => state.generalSlice)

    const { apps, appCategories } = useSelector(
        (state: RootState) => state.appSlice,
    )

    const onRowDoubleClick = useCallback(
        (row: SearchAppInfo) => {
            navigate(
                generatePath(APP_ROUTES.APP_DETAILS, { id: row.id.toString() }),
            )
        },
        [navigate],
    )

    const urlPage = useMemo<number>(
        () => Number.parseInt(searchParams.get('page') || '') || 1,
        [searchParams],
    )

    useEffect(() => {
        dispatch(setIsLoading(true))

        getApps({ page: urlPage, limit: APPS_LIMIT })
            .then(res => {
                setPageCount(res.data.meta.last_page)

                dispatch(
                    setApps(
                        res.data.value.map((app, i) => ({
                            ...app,
                            order: i + 1,
                        })),
                    ),
                )
            })
            .catch(e => handleServerAPIError(e, dispatch, navigate))
            .finally(() => {
                dispatch(setIsLoading(false))
            })
    }, [])

    useEffect(() => {
        if (isTokenExpired()) {
            navigate(APP_ROUTES.LOGIN)
        }
    }, [])

    const getAppImageUrl = useCallback(
        (app: SearchAppInfo) => {
            if (cdnInfo.imageCdn.enable && cdnInfo.imageCdn.url != undefined) {
                const cdnLink = cdnInfo.imageCdn.url
                const cleanCdnLink =
                    cdnLink.charAt(cdnLink.length - 1) === '/'
                        ? cdnLink.slice(0, -1)
                        : cdnLink
                return getAppIconUrl(app.id, cleanCdnLink)
            } else {
                return getAppIconUrl(app.id)
            }
        },
        [cdnInfo, getAppIconUrl],
    )

    return (
        <Container className="no-gutters">
            <Col xs="12">
                <DataGrid
                    columns={columnsConfig(appCategories, getAppImageUrl)}
                    rows={apps}
                    onRowClick={onRowDoubleClick}
                    renderers={{ noRowsFallback: <NoGridData /> }}
                    style={{ height: '100%', minHeight: '500px' }}
                />
            </Col>
            <Col className="align-self-center">
                <SearchPagination
                    limit={APPS_LIMIT}
                    page={urlPage}
                    pages={pageCount}
                    link={APP_ROUTES.VIEW_APPS_EDITOR_PAGE}
                />
            </Col>
        </Container>
    )
}

export default AppsList
